import { React, useCallback, useState, useMemo, Box, Typography, DownloadIcon, Button, CustomButton, } from '../../utils/globalImports';

interface FileUploadProps {
    onUpload: (file: File) => void;
    templateDownloadUrl?: string;
    allowedTypes?: string[];
    maxSizeMB?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
    onUpload,
    templateDownloadUrl,
    allowedTypes = [".csv"],
    maxSizeMB = 5,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Create the accept string (e.g. ".csv")
    const acceptString = useMemo(() => allowedTypes.join(","), [allowedTypes]);

    // Validate a single file for extension and size
    const validateFile = useCallback(
        (file: File): boolean => {
            if (
                !allowedTypes.some((ext) =>
                    file.name.toLowerCase().endsWith(ext.toLowerCase())
                )
            ) {
                setError(`Invalid file type: ${file.name}. Only CSV files are allowed.`);
                return false;
            }
            if (file.size > maxSizeMB * 1024 * 1024) {
                setError(`File too large: ${file.name} (Max: ${maxSizeMB}MB)`);
                return false;
            }
            return true;
        },
        [allowedTypes, maxSizeMB]
    );

    // Process a FileList and only handle the first file
    const handleFiles = useCallback(
        (files: FileList | null) => {
            if (!files || files.length === 0) return;
            const file = files.item(0);
            if (!file) return;
            if (validateFile(file)) {
                setSelectedFile(file);
                setError(null);
            } else {
                setSelectedFile(null);
            }
        },
        [validateFile]
    );

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            handleFiles(event.target.files);
        },
        [handleFiles]
    );

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            handleFiles(event.dataTransfer.files);
        },
        [handleFiles]
    );

    const handleUploadClick = useCallback(() => {
        if (selectedFile) {
            onUpload(selectedFile);
        }
    }, [selectedFile, onUpload]);

    return (
        <Box
            sx={{
                border: "2px dashed #aaa",
                p: 3,
                textAlign: "center",
                borderRadius: 1,
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <input
                type="file"
                multiple={false}
                accept={acceptString}
                style={{ display: "none" }}
                onChange={handleFileChange}
                id="file-upload"
            />
            <label htmlFor="file-upload">
                <CustomButton
                    fullWidth={false}
                    component="span"
                    sx={{
                        mt: 0,
                        mb: 2,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                    }}
                    backgroundColor="#1e66ab"
                    hoverColor="rgba(150, 193, 33, 0.75)"
                    onClick={handleUploadClick}
                >
                    Select File
                </CustomButton>

            </label>

            <Typography variant="body2" sx={{ mt: 2 }}>
                or Drag &amp; Drop a CSV file here
            </Typography>

            {error && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}

            {selectedFile && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1">
                        Selected File: {selectedFile.name} (
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                    </Typography>

                    <CustomButton
                        fullWidth={false}
                        sx={{
                            mt: 0,
                            mb: 2,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={handleUploadClick}
                    >
                        Upload
                    </CustomButton>

                </Box>
            )}

            {templateDownloadUrl && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        href={templateDownloadUrl}
                        startIcon={<DownloadIcon />}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', }}
                    >
                        Download Template
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default React.memo(FileUpload);
